import { queryOptions } from '@tanstack/react-query';

import { gqlFetcher, graphql } from '#features/api';
import { ApiError } from '#features/api/utils/ApiError';
import { persistNativeAuthResult } from '#features/api/utils/nativeAuth';

const USER_NOT_AUTH_CODE = 'user.not.auth';
const USER_BANNED_CODE = 'user.banned';
const TEMPORARY_UNAVAILABLE_STATUS = 503;

export const meDoc = graphql(`
  query Me {
    user {
      me {
        __typename
        ... on CurrentUser {
          about
          avatar
          birthday
          createdAt
          deleted
          displayName
          experience
          gameIds
          gender
          id
          platformsIds
          updatedAt
          email
          emailConfirmed
          verified
          newEmail
          lastVisit
          timeFormat
          timeZoneOffset
          statsVisibility
          sessionId
          token
          interests {
            id
            name
          }
          onboarding {
            profileSaved
            postCreated
          }
          interestsIds
          languages {
            code
            name
            nativeName
          }
          pushToken {
            id
            token
          }
          country {
            code
            icon
            name
          }
          oauth {
            discord {
              id
              email
              displayName
              visible
              type
            }
            google {
              id
              email
              displayName
              visible
              type
            }
            steam {
              id
              email
              displayName
              visible
              type
            }
            epicGames {
              id
              email
              displayName
              visible
              type
            }
            riotGames {
              id
              email
              displayName
              visible
              type
            }
            bungie {
              id
              email
              displayName
              visible
              type
            }
            xbox {
              id
              email
              displayName
              visible
              type
            }
          }
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export const getMyProfile = async () => {
  const me = await gqlFetcher(meDoc).then((data) => data?.user?.me);

  if (me?.__typename === 'GlobalError') {
    if (me.errorCode === USER_NOT_AUTH_CODE) {
      throw new ApiError('Unauthorized', 401);
    }

    // Banned users must not fall into the 503 branch (it retries up to 10 times).
    if (me.errorCode === USER_BANNED_CODE) {
      throw new ApiError(me.errorMessage || 'Banned', 403);
    }

    throw new ApiError(me.errorMessage || 'Unable to load profile', TEMPORARY_UNAVAILABLE_STATUS);
  }

  if (me?.__typename === 'CurrentUser' && me.token) {
    // Шлюз перевыпустил токен (раз в сутки, или впервые выдал ему ключ сеанса). Вебу новый токен
    // уже приехал кукой; это поле шлюз заполняет только для нативной оболочки — она куку не
    // читает и без этого навсегда осталась бы со старым, отозвать который нельзя.
    await persistNativeAuthResult({ __typename: 'Token', token: me.token });
  }

  return me;
};

export const getMeOptions = () =>
  queryOptions({
    queryKey: ['me'],
    staleTime: 1000 * 60 * 5,
    queryFn: getMyProfile,
    retry: (failureCount, error) => {
      if (error instanceof ApiError) {
        if (error.status === TEMPORARY_UNAVAILABLE_STATUS) {
          return failureCount < 10;
        }

        return error.status === 0 && failureCount < 2;
      }

      if (error instanceof Error && error.name === 'TypeError') {
        return failureCount < 2;
      }

      return false;
    },
  });
